import React, { useState } from "react";
import {
  Phone,
  Clock,
  TrendingUp,
  Timer,
  Settings,
  FileText,
  BarChart3,
  PhoneOutgoing,
  PhoneIncoming,
  Mic,
  Radio,
  PieChart,
} from "lucide-react";
import { Capability, QuickAction, StatCard } from "./types";
import { Link } from "react-router-dom";

// Header Component
const DashboardHeader: React.FC<{ phoneNumber: string; status: string }> = ({
  phoneNumber,
  status,
}) => {
  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Voice Channel Dashboard
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage your voice communications
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 px-3 py-1 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            {status}
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {phoneNumber}
          </span>
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard: React.FC<StatCard> = ({
  icon,
  value,
  label,
  trend,
  trendUp,
}) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center">
          {icon}
        </div>
        <span
          className={`text-sm font-medium ${trendUp ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
            }`}
        >
          {trend}
        </span>
      </div>
      <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">{value}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
};

// Quick Actions Component
const QuickActionsGrid: React.FC<{ actions: QuickAction[] }> = ({
  actions,
}) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Quick Actions
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            className="flex flex-col items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-all group"
          >
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-500/15 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
              <div className="text-blue-600 dark:text-blue-400 group-hover:text-white transition-colors">
                {action.icon}
              </div>
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {action.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Capability Card Component
const CapabilityCard: React.FC<Capability> = ({
  icon,
  title,
  description,
  color,
  detailText,
  linkText,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getDetailText = () => {
    switch (title) {
      case "Voice Broadcast":
        return "Deliver pre-recorded messages or text-to-speech broadcasts to your customer base.";
      default:
        return detailText || "";
    }
  };



  const getLinkText = () => {
    switch (title) {
      case "Voice Broadcast":
        return (
          <Link
            to="/campaigns/create"
            className="text-blue-600 dark:text-blue-400 hover:underline text-base"
          >
            Start guide →
          </Link>
        );

      default:
        return (
          <Link
            to={linkText || "/voice-broadcast"}
            className="text-blue-600 dark:text-blue-400 hover:underline text-base"
          >
            {linkText}
          </Link>
        );
    }
  };

  const getCallText = () => {
    switch (title) {
      case "Make Calls":
        return "Use our Voice API to make calls with just a few lines of code. Perfect for customer outreach and sales.";
      default:
        return detailText || "";
    }
  };

  const getLinkCall = () => {
    switch (title) {
      case "Make Calls":
        return (
          <Link
            to="/make/call"
            className="text-blue-600 dark:text-blue-400 hover:underline text-base"
          >
            Start guide →
          </Link>
        );

      default:
        return (
          <Link
            to={linkText || "/voice-broadcast"}
            className="text-blue-600 dark:text-blue-400 hover:underline text-base"
          >
            {linkText}
          </Link>
        );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow relative">
      <div
        className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center mb-4`}
      >
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 dark:text-gray-300">{description}</p>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="mt-2 text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
      >
        {isOpen ? "Hide" : "Get Guide"}
      </button>
      {isOpen && (
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-300">
          {getDetailText()}
          <span className="block mt-2">{getLinkText()}</span>
        </div>
      )}

      {isOpen && (
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-300">
          {getCallText()}
          <span className="block mt-2">{getLinkCall()}</span>
        </div>
      )}
    </div>
  );
};

// Capabilities Section Component
const CapabilitiesSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"overview" | "ivr" | "api">(
    "overview"
  );

  const capabilities: Capability[] = [
    {
      icon: <PhoneOutgoing className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />,
      title: "Make Calls",
      description: "Initiate outbound calls programmatically or manually",
      color: "bg-cyan-100 dark:bg-cyan-500/15",
    },
    {
      icon: <PhoneIncoming className="w-6 h-6 text-blue-600 dark:text-blue-400" />,
      title: "Receive Calls",
      description: "Handle incoming calls with custom routing logic",
      color: "bg-blue-100 dark:bg-blue-500/15",
    },
    {
      icon: <Mic className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />,
      title: "IVR System",
      description: "Build interactive voice response menus",
      color: "bg-cyan-100 dark:bg-cyan-500/15",
    },
    {
      icon: <Radio className="w-6 h-6 text-orange-600 dark:text-orange-400" />,
      title: "Call Recording",
      description: "Record and store calls for quality and compliance",
      color: "bg-orange-100 dark:bg-orange-500/15",
    },
    {
      icon: <Radio className="w-6 h-6 text-blue-600 dark:text-blue-400" />,
      title: "Voice Broadcast",
      description: "Send voice messages to thousands simultaneously",
      color: "bg-blue-100 dark:bg-blue-500/15",
    },
    {
      icon: <PieChart className="w-6 h-6 text-pink-600 dark:text-pink-400" />,
      title: "Analytics & Reports",
      description: "Track call metrics and performance insights",
      color: "bg-pink-100 dark:bg-pink-500/15",
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Voice Channel Capabilities
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "overview"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("ivr")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "ivr"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
          >
            IVR Setup
          </button>
          <button
            onClick={() => setActiveTab("api")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "api"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
          >
            API Integration
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {capabilities.map((capability, index) => (
          <CapabilityCard key={index} {...capability} />
        ))}
      </div>
    </div>
  );
};

// Main Dashboard Component
const VoiceCommunications: React.FC = () => {
  const statsData: StatCard[] = [
    {
      icon: <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      value: "1,234",
      label: "Total Calls Today",
      trend: "+12%",
      trendUp: true,
    },
    {
      icon: <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      value: "45,678",
      label: "Active Minutes",
      trend: "+8%",
      trendUp: true,
    },
    {
      icon: <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      value: "94.2%",
      label: "Answer Rate",
      trend: "+2.3%",
      trendUp: true,
    },
    {
      icon: <Timer className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      value: "3m 42s",
      label: "Avg Duration",
      trend: "-5%",
      trendUp: false,
    },
  ];

  const quickActions: QuickAction[] = [
    {
      icon: <Phone className="w-6 h-6" />,
      label: "Make Test Call",
      onClick: () => alert("Making test call..."),
    },
    {
      icon: <Settings className="w-6 h-6" />,
      label: "Configure IVR",
      onClick: () => alert("Opening IVR configuration..."),
    },
    {
      icon: <FileText className="w-6 h-6" />,
      label: "View API Docs",
      onClick: () => alert("Opening API documentation..."),
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      label: "View Analytics",
      onClick: () => alert("Opening analytics..."),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      <DashboardHeader phoneNumber="+91 98765 43210" status="Active" />

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsData.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>

        {/* Quick Actions */}
        <QuickActionsGrid actions={quickActions} />

        {/* Capabilities Section */}
        <CapabilitiesSection />
      </div>
    </div>
  );
};

export default VoiceCommunications;
